import { useCallback, useEffect, useMemo, useState } from 'react';

import { faCircleXmark, faDownload, faTooth, faTriangleExclamation } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import {
  Button,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem,
  Toast,
} from '@repo/videa-components';
import { toast } from 'sonner';

import {
  ZuubBenefitSummaryV4,
  ZuubManualCheckDetailsBenefitSummary,
  ZuubManualCheckDetailsItem,
  ZuubManualCheckListPatient,
  ZuubOrthoBenefitSummary,
  ZuubTreatmentHistory,
  ZuubCategoryLimitationsV4,
  ZuubDisclaimersAndLimitationsV4,
  ZuubMaximumsAndDeductiblesV4,
  ZuubWaitingPeriodRowV4,
  StatusType,
  StatusTypeValue,
} from '@/@types/zuub/contracts';

import { useZuubManualCheckDetails } from '@/react-query/rcm/useZuubManualCheckDetails';

import { StatusChip } from '@/common-components/elements/StatusChip/StatusChip';
import {
  getInsuranceStatusCtaDisplay,
  getStatusTypeVariant,
} from '@/modules/autoVerify/appointmentsList/AutoVerifyTable';
import { getVariantStyles } from '@/common-components/elements/StatusChip/helpers';
import { useZuubManualCheckStatus } from '@/react-query/rcm/useZuubManualCheckStatus';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { parseAndFormatDate } from '@/utils/parseDate';
import { useCoverageReportPdf } from './useCoverageReportPdf';


const PLACEHOLDER = '--';
const PLACEHOLDER_PCT = '--%';
const PLACEHOLDER_CURRENCY = '$--';

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return PLACEHOLDER_CURRENCY;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}


const METRIC_CARD_LABELS = [
  'Benefits Used',
  'Benefits Remaining',
  'Deductible',
  'Waiting Period',
] as const;

const COVERAGE_ROWS = [
  { label: 'Preventive', value: PLACEHOLDER_PCT },
  { label: 'Basic', value: PLACEHOLDER_PCT },
  { label: 'Major', value: PLACEHOLDER_PCT },
  { label: 'Orthodontics', value: PLACEHOLDER_PCT },
] as const;

const POLICY_PROVISIONS = [
  'Missing tooth clause',
  'Coordination of Benefits',
] as const;

const FREQUENCY_LIMITATION_PROCEDURES = [
  'Periodic Exam',
  'Adult Prophylaxis',
  'Bitewings - Four Films',
] as const;

const TREATMENT_HISTORY_ROW_COUNT = 3;

const FULL_REPORT_BENEFIT_CATEGORIES = [
  'Preventive',
  'Basic',
  'Major',
  'Orthodontics',
] as const;

const NETWORK_SCOPE_IN = 'InNetwork' as const;
const NETWORK_SCOPE_OUT = 'OutOfNetwork' as const;
export type NetworkScope = typeof NETWORK_SCOPE_IN | typeof NETWORK_SCOPE_OUT;

const PATIENT_INFO_GRID_LABELS = [
  { label: 'Patient Name', key: 'patientName' as const },
  { label: 'DOB', key: 'dob' as const },
  { label: 'Member ID', key: 'memberId' as const },
  { label: 'Group ID', key: 'groupId' as const },
  { label: 'Subscriber Name', key: 'subscriberName' as const },
  { label: 'Subscriber DOB', key: 'subscriberDob' as const },
  { label: 'Effective Date', key: 'effectiveDate' as const },
  { label: 'Policy Period', key: 'policyPeriod' as const },
  { label: 'Benefit Period Type', key: 'benefitPeriodType' as const },
  { label: 'Payer ID', key: 'payerId' as const },
  { label: 'Claims Address', key: 'claimsAddress' as const },
] as const;

function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  return parseAndFormatDate(iso, 'MM/dd/yyyy') || PLACEHOLDER;
}

function formatWaitingPeriodValue(
  periods:
    | { serviceType: string; startDate: string | null; endDate: string | null }[]
    | null
    | undefined,
): string {
  if (!periods?.length) return PLACEHOLDER;
  return periods
    .map(
      (p) =>
        `${p.serviceType}: ${formatDateOnly(p.startDate)} – ${formatDateOnly(p.endDate)}`,
    )
    .join('; ');
}

function getPatientInfoGridValuesFromList(
  patient: ZuubManualCheckListPatient | null,
): { label: string; value: string }[] {
  const plan = patient?.plans[0];
  const payerIdStr =
    plan?.payerId != null ? String(plan.payerId) : PLACEHOLDER;
  const values: Record<(typeof PATIENT_INFO_GRID_LABELS)[number]['key'], string> = {
    patientName: patient?.patientName ?? PLACEHOLDER,
    dob: PLACEHOLDER,
    memberId: PLACEHOLDER,
    groupId: PLACEHOLDER,
    subscriberName: PLACEHOLDER,
    subscriberDob: PLACEHOLDER,
    effectiveDate: PLACEHOLDER,
    policyPeriod: PLACEHOLDER,
    benefitPeriodType: PLACEHOLDER,
    payerId: payerIdStr,
    claimsAddress: PLACEHOLDER,
  };
  return PATIENT_INFO_GRID_LABELS.map(({ label, key }) => ({
    label,
    value: values[key],
  }));
}

function getPatientInfoGridValuesFromDetails(
  detail: ZuubManualCheckDetailsItem | null,
): { label: string; value: string }[] {
  if (!detail) return getPatientInfoGridValuesFromList(null);
  const p = detail.patient;
  const plan = detail.plan;
  const sub = detail.subscriber;
  const addr = detail.claimsAddress;
  const subscriberName =
    sub?.firstName != null && sub?.lastName != null
      ? `${sub.firstName} ${sub.lastName}`.trim()
      : sub?.firstName ?? sub?.lastName ?? PLACEHOLDER;
  const claimsAddressStr = addr
    ? [addr.line1, addr.city, addr.state, addr.zipCode]
        .filter(Boolean)
        .join(', ') || PLACEHOLDER
    : PLACEHOLDER;
  const values: Record<(typeof PATIENT_INFO_GRID_LABELS)[number]['key'], string> = {
    patientName: p?.displayName ?? (p ? `${p.firstName} ${p.lastName}`.trim() : PLACEHOLDER),
    dob: formatDateOnly(p?.dateOfBirth ?? null),
    memberId: p?.memberId ?? PLACEHOLDER,
    groupId: plan?.groupNumber ?? PLACEHOLDER,
    subscriberName,
    subscriberDob: formatDateOnly(sub?.dateOfBirth ?? null),
    effectiveDate: formatDateOnly(plan?.effectiveDate ?? null),
    policyPeriod:
      plan?.policyStartDate != null && plan?.policyEndDate != null
        ? `${formatDateOnly(plan.policyStartDate)} - ${formatDateOnly(plan.policyEndDate)}`
        : PLACEHOLDER,
    benefitPeriodType: plan?.benefitPeriodType ?? PLACEHOLDER,
    payerId: detail.payerId != null ? String(detail.payerId) : PLACEHOLDER,
    claimsAddress: claimsAddressStr,
  };
  return PATIENT_INFO_GRID_LABELS.map(({ label, key }) => ({
    label,
    value: values[key],
  }));
}

function FullReportEmptySection({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <div className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
      <h3 className="vai-mb-4 vai-font-bold vai-text-gray-900">
        <Trans id={title}>{title}</Trans>
      </h3>
      <div className="vai-flex vai-items-center vai-justify-center vai-rounded-md vai-bg-gray-100 vai-py-8 vai-text-sm vai-text-muted-foreground">
        <Trans id={message}>{message}</Trans>
      </div>
    </div>
  );
}

export interface PatientCoverageDataCardProps {
  selectedPatient?: ZuubManualCheckListPatient | null;
}

export const PatientCoverageDataCard = ({
  selectedPatient = null,
}: PatientCoverageDataCardProps) => {
  const [planSummaryView, setPlanSummaryView] = useState<
    'dental-care' | 'orthodontics'
  >('dental-care');
  const [networkScope, setNetworkScope] = useState<NetworkScope>(NETWORK_SCOPE_IN);

  const requestId =
    selectedPatient?.plans[0]?.requestId ??
    selectedPatient?.unifiedAppointmentId ??
    null;
  const { data: detailsData, isLoading: detailsLoading, isError: detailsError } =
    useZuubManualCheckDetails(requestId);

  const { data: statusData } = useZuubManualCheckStatus(requestId);
  const asyncStatus = statusData?.asyncStatus ?? null;

  const detail = detailsData?.detailedData?.[0] ?? null;
  const listPlan = selectedPatient?.plans[0];

  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const { contentRef, isGenerating, downloadPdf } = useCoverageReportPdf();

  const handleDownloadPdf = useCallback(async () => {
    const patientName = detail?.patient?.displayName
      ?? selectedPatient?.patientName
      ?? null;
    const namePart = patientName
      ? `_${patientName.replace(/\s+/g, '_')}`
      : '';
    const datePart = format(new Date(), 'yyyy-MM-dd');
    const fileName = `Coverage_Report${namePart}_${datePart}.pdf`;

    trackEvent(mixpanelEvents.autoVerifyCoverageReportDownloadPdf);

    try {
      await downloadPdf(fileName);
    } catch (error) {
      console.error('Failed to generate coverage report PDF:', error);
      toast(
        <Toast
          variant="error"
          title={t`PDF generation failed`}
          description={t`Failed to generate PDF. Please try again.`}
        />,
        {
          closeButton: true,
          position: 'top-right',
          style: { width: '460px' },
        },
      );
    }
  }, [detail, selectedPatient, trackEvent, downloadPdf, t]);

  const networks = useMemo(() => detail?.networks ?? [], [detail]);
  const inNetworkBlock = networks.find((n) => n.status === NETWORK_SCOPE_IN) ?? null;
  const outOfNetworkBlock = networks.find((n) => n.status === NETWORK_SCOPE_OUT) ?? null;
  const hasAnyNetwork = inNetworkBlock != null || outOfNetworkBlock != null;

  const selectedNetwork =
    networks.find((n) => n.status === networkScope) ?? null;

  useEffect(() => {
    if (!detail) return;
    const currentSelected = networks.find((n) => n.status === networkScope);
    if (currentSelected != null) return;
    if (inNetworkBlock != null) setNetworkScope(NETWORK_SCOPE_IN);
    else if (outOfNetworkBlock != null) setNetworkScope(NETWORK_SCOPE_OUT);
  }, [detail, networks, networkScope, inNetworkBlock, outOfNetworkBlock]);

  const dentalBenefitSummary =
    selectedNetwork?.benefitSummary ?? detail?.benefitSummary;
  const orthoBenefitSummary =
    selectedNetwork?.orthoBenefitSummary ?? detail?.orthoBenefitSummary;
  /** Dental vs ortho summaries come from network or detail with different Zuub shapes. */
  const benefitSummary:
    | ZuubBenefitSummaryV4
    | ZuubManualCheckDetailsBenefitSummary
    | ZuubOrthoBenefitSummary
    | null
    | undefined =
    planSummaryView === 'orthodontics' ? orthoBenefitSummary : dentalBenefitSummary;
  const deductible = benefitSummary?.deductible;
  const metricCards = [
    {
      label: METRIC_CARD_LABELS[0],
      value:
        benefitSummary?.usedBenefits != null
          ? formatCurrency(benefitSummary.usedBenefits)
          : PLACEHOLDER_CURRENCY,
    },
    {
      label: METRIC_CARD_LABELS[1],
      value:
        benefitSummary?.remainingBenefits != null
          ? formatCurrency(benefitSummary.remainingBenefits)
          : listPlan?.remainingBenefits != null
            ? formatCurrency(listPlan.remainingBenefits)
            : PLACEHOLDER_CURRENCY,
    },
    {
      label: METRIC_CARD_LABELS[2],
      value:
        deductible != null
          ? `${formatCurrency(deductible.remainingDeductible ?? null)} / ${formatCurrency(deductible.totalDeductible ?? null)}`
          : listPlan?.remainingDeductible != null || listPlan?.totalDeductible != null
            ? `${formatCurrency(listPlan?.remainingDeductible ?? null)} / ${formatCurrency(listPlan?.totalDeductible ?? null)}`
            : PLACEHOLDER_CURRENCY,
    },
    {
      label: METRIC_CARD_LABELS[3],
      value: PLACEHOLDER,
    },
  ];

  const lastRunSource = detailsData?.lastRun ?? selectedPatient?.lastRun;
  const lastRunFormatted =
    lastRunSource != null
      ? format(new Date(lastRunSource), 'MMM d, yyyy')
      : PLACEHOLDER;

  const insuranceStatusV2 = detail?.insuranceStatusV2 ?? listPlan?.insuranceStatusV2 ?? null;
  const statusType = (listPlan?.statusType ?? StatusType.ACTION) as StatusTypeValue;
  const statusVariant = getStatusTypeVariant(statusType);
  const statusText = insuranceStatusV2
    ? getInsuranceStatusCtaDisplay(insuranceStatusV2)
    : 'Unknown';
  const statusStyles = getVariantStyles(statusVariant);

  const patientInfoGrid = detail
    ? getPatientInfoGridValuesFromDetails(detail)
    : getPatientInfoGridValuesFromList(selectedPatient);

  const patientDisplayName =
    detailsData?.patientName ??
    selectedPatient?.patientName ??
    (detail?.patient
      ? `${detail.patient.firstName} ${detail.patient.lastName}`.trim()
      : PLACEHOLDER);
  const payerName =
    detail?.payerName ?? detail?.plan?.payerName ?? listPlan?.payerName ?? PLACEHOLDER;

  const effectiveCoverageTiers =
    selectedNetwork?.coverageTiers ?? detail?.coverageTiers;
  const coverageRowsFromTiers = effectiveCoverageTiers?.length
    ? effectiveCoverageTiers.map((t) => ({
        label: t.tier,
        value: t.coInsurance != null ? `${t.coInsurance}%` : PLACEHOLDER_PCT,
      }))
    : null;
  const coverageRows = coverageRowsFromTiers ?? COVERAGE_ROWS;

  const waitingPeriodValue = formatWaitingPeriodValue(
    selectedNetwork?.waitingPeriods,
  );
  const policyProvisionRows: { label: string; value: string }[] = [
    {
      label: POLICY_PROVISIONS[0],
      value: detail?.missingToothClause?.trim() || PLACEHOLDER,
    },
    {
      label: POLICY_PROVISIONS[1],
      value:
        detail?.coordinationOfBenefits?.paymentOrder?.trim() || PLACEHOLDER,
    },
  ];

  const coverageSummaryCategories = selectedNetwork?.coverage ?? [];
  const hasCoverageSummary = coverageSummaryCategories.length > 0;

  const coverageSummaryFlatRows = coverageSummaryCategories.flatMap((cat) =>
    (cat.services ?? []).map((s) => ({
      service: s.serviceName,
      pct:
        (s.coInsurance ?? cat.coInsurance ?? s.planCoverage) != null
          ? `${s.coInsurance ?? cat.coInsurance ?? s.planCoverage}%`
          : PLACEHOLDER_PCT,
    })),
  );
  const coverageSummaryMid = Math.ceil(coverageSummaryFlatRows.length / 2);
  const coverageSummaryLeft = coverageSummaryFlatRows.slice(0, coverageSummaryMid);
  const coverageSummaryRight = coverageSummaryFlatRows.slice(coverageSummaryMid);

  const treatmentHistoryRaw = useMemo(
    () => (detail?.treatmentHistoryV2 ?? []) as ZuubTreatmentHistory[],
    [detail],
  );
  const treatmentHistoryRows = treatmentHistoryRaw
    .flatMap((entry) =>
      (entry.procedures ?? []).map((proc) => ({
        code: proc.code || PLACEHOLDER,
        date: formatDateOnly(entry.date),
        procedure: proc.name?.trim() || proc.code || PLACEHOLDER,
        location: proc.location?.trim() || PLACEHOLDER,
        surface: proc.surface?.trim() || PLACEHOLDER,
      })),
    )
    .slice(0, 10);

  // Lookup: ADA code -> list of treatment history entries for that code
  const treatmentHistoryByCode = useMemo(() => {
    const map = new Map<string, { date: string; location: string }[]>();
    for (const entry of treatmentHistoryRaw) {
      for (const proc of entry.procedures ?? []) {
        if (!proc.code) continue;
        const existing = map.get(proc.code) ?? [];
        existing.push({
          date: formatDateOnly(entry.date),
          location: proc.location?.trim() || 'Full mouth',
        });
        map.set(proc.code, existing);
      }
    }
    return map;
  }, [treatmentHistoryRaw]);

  // Extract data from selected network for full report sections
  const maximumsAndDeductibles: ZuubMaximumsAndDeductiblesV4 | null =
    selectedNetwork?.maximumsAndDeductibles ?? null;
  const disclaimersAndLimitations: ZuubDisclaimersAndLimitationsV4 | null =
    selectedNetwork?.disclaimersAndLimitations ?? null;
  const waitingPeriods: ZuubWaitingPeriodRowV4[] =
    selectedNetwork?.waitingPeriods ?? [];

  // Build a lookup from category name -> limitations for that category
  const limitationsByCategoryName = useMemo(() => {
    const map = new Map<string, ZuubCategoryLimitationsV4>();
    if (!disclaimersAndLimitations?.categoryLimitations) return map;
    for (const cat of disclaimersAndLimitations.categoryLimitations) {
      // Store by categoryName to match against coverage categories
      map.set(cat.categoryName, cat);
    }
    return map;
  }, [disclaimersAndLimitations]);

  const frequencyLimitations = limitationsByCategoryName.get('Frequency');
  const frequencyLimitationRows = FREQUENCY_LIMITATION_PROCEDURES.map((name) => {
    const match = frequencyLimitations?.limitations.find(
      (l) => l.procedureName === name,
    );
    let value = match?.limitation ?? PLACEHOLDER;
    if (match?.limitation) {
      value = match.limitation
        .replace(/^Limited to\s*/i, '')
        .replace(/\s*Procedures?\s*/gi, ' ')
        .replace('floatingmonths', 'months')
        .replace('planyear', 'year')
        .trim();
    }
    return { label: name, value };
  });

  // Plan type label for coverage column header
  const planTypeLabel = detail?.plan?.planType ?? 'PPO';

  return (
    <div className="vai-w-full vai-rounded-lg vai-border vai-border-gray-200 vai-bg-gray-50 vai-p-6 vai-shadow-sm">
      <div className="vai-flex vai-flex-wrap vai-items-center vai-justify-between vai-gap-4 vai-mb-4">
        <div className="vai-flex vai-items-center vai-gap-3">
          <p className="vai-text-lg vai-font-semibold vai-text-gray-900">
            {patientDisplayName}
          </p>
          <p className="vai-text-sm vai-text-muted-foreground">
            {payerName}
          </p>
          <StatusChip
            variant={statusVariant}
            text={statusText}
          />
        </div>
        <div className="vai-flex vai-items-center vai-gap-3">
          <span className="vai-text-sm vai-text-muted-foreground">
            {lastRunSource
              ? `Verified ${lastRunFormatted}`
              : PLACEHOLDER}
          </span>
          <Button
            variant="outline"
            size="sm"
            data-pdf-hide="true"
            disabled={isGenerating || detailsLoading || !detailsData}
            onClick={handleDownloadPdf}
          >
            {isGenerating ? (
              <span className="vai-mr-2 vai-h-4 vai-w-4 vai-animate-spin vai-rounded-full vai-border-2 vai-border-gray-600 vai-border-t-transparent" />
            ) : (
              <FontAwesomeIcon
                icon={faDownload}
                className="vai-mr-2 vai-h-4 vai-w-4"
                aria-hidden
              />
            )}
            <Trans>{isGenerating ? 'Generating...' : 'Download as PDF'}</Trans>
          </Button>
        </div>
      </div>

      {requestId && detailsLoading && (
        <div className="vai-mb-4 vai-flex vai-items-center vai-gap-2 vai-rounded-md vai-bg-blue-50 vai-px-4 vai-py-3 vai-text-sm vai-text-blue-800">
          <span className="vai-h-4 vai-w-4 vai-animate-spin vai-rounded-full vai-border-2 vai-border-blue-600 vai-border-t-transparent" />
          <Trans>Loading coverage data...</Trans>
        </div>
      )}
      {requestId && detailsError && (
        <div className="vai-mb-4 vai-rounded-md vai-bg-red-50 vai-px-4 vai-py-3 vai-text-sm vai-text-red-800">
          <Trans>Failed to load coverage details.</Trans>
        </div>
      )}

      <Tabs defaultValue="summary" className="vai-w-full">
        <div className="vai-mb-4 vai-flex vai-items-center vai-justify-between vai-gap-4 vai-flex-wrap">
          <TabsList className="vai-w-fit vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-0 vai-gap-0">
            <TabsTrigger
              value="summary"
              className="vai-rounded-r-none vai-rounded-l-md vai-px-4 vai-text-gray-900 vai-bg-white data-[state=active]:vai-bg-blue-50 data-[state=active]:vai-text-blue-700 data-[state=active]:vai-shadow-none"
            >
              <Trans>Summary</Trans>
            </TabsTrigger>
            <TabsTrigger
              value="full-report"
              className="vai-rounded-l-none vai-rounded-r-md vai-px-4 vai-text-gray-900 vai-bg-white data-[state=active]:vai-bg-blue-50 data-[state=active]:vai-text-blue-700 data-[state=active]:vai-shadow-none"
            >
              <Trans>Full Report</Trans>
            </TabsTrigger>
          </TabsList>
          {hasAnyNetwork && (
            <ToggleGroup
              type="single"
              value={networkScope}
              onValueChange={(v) => v && setNetworkScope(v as NetworkScope)}
              className="vai-flex vai-gap-0 vai-w-fit vai-rounded-lg vai-border vai-border-gray-200 !vai-justify-start"
            >
              <ToggleGroupItem
                value={NETWORK_SCOPE_IN}
                disabled={inNetworkBlock == null}
                className="vai-rounded-r-none vai-px-4 vai-text-gray-900 vai-bg-white data-[state=on]:vai-bg-blue-50 data-[state=on]:vai-text-blue-700"
              >
                <Trans>In Network</Trans>
              </ToggleGroupItem>
              <ToggleGroupItem
                value={NETWORK_SCOPE_OUT}
                disabled={outOfNetworkBlock == null}
                className="vai-rounded-l-none vai-px-4 vai-text-gray-900 vai-bg-white data-[state=on]:vai-bg-blue-50 data-[state=on]:vai-text-blue-700"
              >
                <Trans>Out of Network</Trans>
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>

        <TabsContent value="summary" className="vai-mt-0">
          {/* Four metric cards */}
          <div className="vai-flex vai-gap-4 vai-mb-6 vai-flex-wrap">
            {metricCards.map(({ label, value }) => (
              <div
                key={label}
                className="vai-min-w-0 vai-flex-1 vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-4 vai-shadow-sm"
              >
                <p className="vai-text-xs vai-font-medium vai-uppercase vai-text-muted-foreground vai-mb-1">
                  <Trans id={label}>{label}</Trans>
                </p>
                <p className="vai-text-lg vai-font-semibold vai-text-gray-900">
                  {value}
                </p>
              </div>
            ))}
          </div>

          {/* Three sections: Coverage, Policy Provisions, Recent Treatment History */}
          <div className="vai-flex vai-gap-4 vai-flex-wrap">
            {/* Coverage */}
            <div className="vai-min-w-0 vai-flex-1 vai-min-w-[200px] vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-4 vai-shadow-sm">
              <div className="vai-flex vai-items-center vai-justify-between vai-mb-3">
                <h3 className="vai-font-bold vai-text-gray-900">
                  <Trans>Coverage</Trans>
                </h3>
                <span className="vai-text-sm vai-text-muted-foreground">
                  <Trans>Est. %</Trans>
                </span>
              </div>
              <ul className="vai-space-y-2">
                {coverageRows.map(({ label, value }) => (
                  <li
                    key={label}
                    className="vai-flex vai-items-center vai-justify-between vai-text-sm"
                  >
                    <span className="vai-text-foreground">
                      <Trans id={label}>{label}</Trans>
                    </span>
                    <span className="vai-text-foreground">{value}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Policy Provisions */}
            <div className="vai-min-w-0 vai-flex-1 vai-min-w-[200px] vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-4 vai-shadow-sm">
              <h3 className="vai-font-bold vai-text-gray-900 vai-mb-3">
                <Trans>Policy Provisions</Trans>
              </h3>
              <ul className="vai-space-y-2 vai-text-sm vai-text-foreground">
                {policyProvisionRows.map(({ label, value }) => (
                  <li
                    key={label}
                    className="vai-flex vai-items-center vai-justify-between"
                  >
                    <span className="vai-text-foreground">
                      <Trans id={label}>{label}</Trans>
                    </span>
                    <span className="vai-text-foreground">{value}</span>
                  </li>
                ))}
              </ul>
              <div className="vai-mt-4">
                <p className="vai-font-medium vai-text-gray-900 vai-mb-2">
                  <Trans>Frequency Limitations</Trans>
                </p>
                <ul className="vai-space-y-1 vai-text-sm vai-text-foreground vai-pl-2">
                  {frequencyLimitationRows.map(({ label, value }) => (
                    <li
                      key={label}
                      className="vai-flex vai-items-center vai-justify-between"
                    >
                      <span className="vai-text-foreground">{label}</span>
                      <span className="vai-font-medium vai-text-foreground">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Recent Treatment History */}
            <div className="vai-min-w-0 vai-flex-1 vai-min-w-[280px] vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-4 vai-shadow-sm">
              <h3 className="vai-font-bold vai-text-gray-900 vai-mb-3">
                <Trans>Recent Treatment History</Trans>
              </h3>
              <div className="vai-w-full vai-overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                        <Trans>Date</Trans>
                      </TableHead>
                      <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                        <Trans>Procedure</Trans>
                      </TableHead>
                      <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                        <Trans>Tooth/Quad</Trans>
                      </TableHead>
                      <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                        <Trans>Surface</Trans>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {treatmentHistoryRows.length > 0
                      ? treatmentHistoryRows.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {row.date}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {row.procedure}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {row.location}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {row.surface}
                            </TableCell>
                          </TableRow>
                        ))
                      : Array.from({
                          length: TREATMENT_HISTORY_ROW_COUNT,
                        }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {PLACEHOLDER}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {PLACEHOLDER}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {PLACEHOLDER}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                              {PLACEHOLDER}
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="full-report" className="vai-mt-0">
          <div ref={contentRef} className="vai-space-y-6">
            {/* Async status banner */}
            {(asyncStatus === 'failed' || 
            asyncStatus === 'not_supported') && (
              <div className="vai-flex vai-items-center vai-gap-2 vai-rounded-md vai-bg-red-50 vai-px-4 vai-py-3 vai-text-sm vai-text-red-800">
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  className="vai-h-4 vai-w-4 vai-text-red-600"
                  aria-hidden
                />
                <span>
                  <span className="vai-font-semibold"><Trans>Unable to sync full report.</Trans></span>{' '}
                  <Trans>Check directly with payer for benefits information, or retry later.</Trans>
                </span>
              </div>
            )}
            {asyncStatus === 'pending' && (
              <div className="vai-flex vai-items-center vai-gap-2 vai-rounded-md vai-bg-blue-50 vai-px-4 vai-py-3 vai-text-sm vai-text-blue-800">
                <span className="vai-h-4 vai-w-4 vai-animate-spin vai-rounded-full vai-border-2 vai-border-blue-600 vai-border-t-transparent" />
                <span>
                  <span className="vai-font-semibold"><Trans>Syncing full report data.</Trans></span>{' '}
                  <Trans>This may take a few minutes.</Trans>
                </span>
              </div>
            )}

            {/* Plan Summary */}
            <div className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
              <div className="vai-mb-4 vai-flex vai-items-center vai-justify-between">
                <h3 className="vai-font-bold vai-text-gray-900">
                  <Trans>Plan Summary</Trans>
                </h3>
                <ToggleGroup
                  type="single"
                  value={planSummaryView}
                  onValueChange={(v) =>
                    v && setPlanSummaryView(v as 'dental-care' | 'orthodontics')
                  }
                  className="vai-flex vai-gap-0 vai-w-fit vai-rounded-lg vai-border vai-border-gray-200 !vai-justify-start"
                >
                  <ToggleGroupItem
                    value="dental-care"
                    className="vai-rounded-r-none vai-px-4 vai-text-gray-900 vai-bg-white data-[state=on]:vai-bg-blue-50 data-[state=on]:vai-text-blue-700"
                  >
                    <Trans>Dental Care</Trans>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="orthodontics"
                    className="vai-rounded-l-none vai-px-4 vai-text-gray-900 vai-bg-white data-[state=on]:vai-bg-blue-50 data-[state=on]:vai-text-blue-700"
                  >
                    <Trans>Orthodontics</Trans>
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              <div className="vai-space-y-4">
                <div className="vai-grid vai-grid-cols-1 vai-gap-4 sm:vai-grid-cols-3">
                  <div>
                    <p className="vai-text-xs vai-font-medium vai-uppercase vai-text-muted-foreground vai-mb-1">
                      <Trans>Plan Status</Trans>
                    </p>
                    <p className="vai-flex vai-items-center vai-gap-2 vai-font-semibold vai-text-gray-900">
                      <FontAwesomeIcon
                        icon={statusStyles.icon}
                        className={`vai-h-4 vai-w-4 ${statusStyles.iconColor}`}
                        aria-hidden
                      />
                      {statusText}
                    </p>
                    <p className="vai-text-xs vai-text-muted-foreground">
                      <Trans>Last run</Trans>: {lastRunFormatted}
                    </p>
                  </div>
                  <div>
                    <p className="vai-text-xs vai-font-medium vai-uppercase vai-text-muted-foreground vai-mb-1">
                      <Trans>{planSummaryView === 'orthodontics' ? 'Ortho Benefits Remaining' : 'Dental Benefits Remaining'}</Trans>
                    </p>
                    <p className="vai-font-semibold vai-text-gray-900">
                      {benefitSummary?.remainingBenefits != null
                        ? formatCurrency(benefitSummary.remainingBenefits)
                        : listPlan?.remainingBenefits != null
                          ? formatCurrency(listPlan.remainingBenefits)
                          : PLACEHOLDER_CURRENCY}{' '}
                      /{' '}
                      {benefitSummary?.totalBenefits != null
                        ? formatCurrency(benefitSummary.totalBenefits)
                        : PLACEHOLDER_CURRENCY}
                    </p>
                    <Progress
                      value={
                        benefitSummary?.percentageUsed != null
                          ? 100 - benefitSummary.percentageUsed
                          : listPlan?.remainingBenefits != null && listPlan.remainingBenefits > 0
                            ? 50
                            : 0
                      }
                      className="vai-mt-2 vai-bg-gray-200"
                      indicatorClassName="vai-bg-green-500"
                    />
                  </div>
                  <div>
                    <p className="vai-text-xs vai-font-medium vai-uppercase vai-text-muted-foreground vai-mb-1">
                      <Trans>Deductible Remaining</Trans>
                    </p>
                    <p className="vai-font-semibold vai-text-gray-900">
                      {formatCurrency(deductible?.remainingDeductible ?? listPlan?.remainingDeductible ?? null)} /{' '}
                      {formatCurrency(deductible?.totalDeductible ?? listPlan?.totalDeductible ?? null)}
                    </p>
                    <Progress
                      value={
                        deductible?.percentageMet != null ? 100 - deductible.percentageMet : 0
                      }
                      className="vai-mt-2 vai-bg-gray-200"
                      indicatorClassName="vai-bg-green-500"
                    />
                  </div>
                </div>
                <div className="vai-flex vai-gap-4 vai-flex-wrap">
                  {FULL_REPORT_BENEFIT_CATEGORIES.map((label) => {
                    const tierMatch = effectiveCoverageTiers?.find(
                      (t) => t.tier.toLowerCase() === label.toLowerCase(),
                    );
                    const pct =
                      tierMatch?.coInsurance != null
                        ? `${tierMatch.coInsurance}%`
                        : PLACEHOLDER_PCT;
                    return (
                      <div
                        key={label}
                        className="vai-min-w-0 vai-flex-1 vai-rounded-md vai-border vai-border-gray-200 vai-bg-gray-50 vai-p-3"
                      >
                        <p className="vai-text-xs vai-font-medium vai-uppercase vai-text-muted-foreground vai-mb-1">
                          <Trans id={label}>{label}</Trans>
                        </p>
                        <p className="vai-font-medium vai-text-gray-900">
                          {pct}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Patient Information */}
            <div id="patient-information-section" className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
              <h3 className="vai-mb-4 vai-font-bold vai-text-gray-900">
                <Trans>Patient Information</Trans>
              </h3>
              <div className="vai-grid vai-grid-cols-2 vai-gap-x-6 vai-gap-y-3 sm:vai-grid-cols-3">
                {patientInfoGrid.map(({ label, value }) => (
                  <div key={label}>
                    <p className="vai-text-xs vai-text-muted-foreground">
                      <Trans id={label}>{label}</Trans>
                    </p>
                    <p className="vai-text-sm vai-font-medium vai-text-gray-900">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Coverage Summary - SERVICE + COVERAGE in two pillars */}
            {hasCoverageSummary ? (
              <div id="coverage-summary-section" className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
                <h3 className="vai-mb-4 vai-font-bold vai-text-gray-900">
                  <Trans>Coverage Summary</Trans>
                </h3>
                <div className="vai-flex vai-gap-6 vai-flex-wrap">
                  <div className="vai-min-w-0 vai-flex-1 vai-min-w-[200px] vai-overflow-hidden vai-rounded-lg vai-border vai-border-gray-200">
                    <Table>
                      <TableHeader>
                        <TableRow className="vai-bg-gray-50">
                          <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                            <Trans>Service</Trans>
                          </TableHead>
                          <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                            <Trans>Coverage</Trans>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coverageSummaryLeft.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="vai-text-sm vai-text-foreground">
                              {row.service}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground">
                              {row.pct}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="vai-min-w-0 vai-flex-1 vai-min-w-[200px] vai-overflow-hidden vai-rounded-lg vai-border vai-border-gray-200">
                    <Table>
                      <TableHeader>
                        <TableRow className="vai-bg-gray-50">
                          <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                            <Trans>Service</Trans>
                          </TableHead>
                          <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-sm vai-uppercase">
                            <Trans>Coverage</Trans>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coverageSummaryRight.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="vai-text-sm vai-text-foreground">
                              {row.service}
                            </TableCell>
                            <TableCell className="vai-text-sm vai-text-foreground">
                              {row.pct}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            ) : (
              <FullReportEmptySection
                title="Coverage Summary"
                message="No coverage details to display."
              />
            )}

            {/* Coverage Details - per-category sections */}
            {hasCoverageSummary ? (
              <div id="coverage-details" className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
                <h3 className="vai-mb-6 vai-font-bold vai-text-gray-900">
                  <Trans>Coverage Details</Trans>
                </h3>
                <div className="vai-space-y-8">
                  {coverageSummaryCategories.map((cat, catIdx) => {
                    // Find frequency limitations matching services in this category
                    const frequencyLimitations = limitationsByCategoryName.get('Frequency');
                    const serviceCodes = new Set(
                      (cat.services ?? []).map((s) => s.adaCode).filter(Boolean),
                    );
                    const relevantProvisions = frequencyLimitations?.limitations.filter(
                      (l) => serviceCodes.has(l.procedureCode),
                    );

                    return (
                      <div key={`cat-${catIdx}`}>
                        <h4 className="vai-mb-3 vai-font-bold vai-text-gray-900">
                          {cat.categoryName}
                        </h4>
                        <div className="vai-overflow-hidden vai-rounded-lg vai-border vai-border-gray-200">
                          <Table>
                            <TableHeader>
                              <TableRow className="vai-bg-gray-50">
                                <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                  <Trans>Service</Trans>
                                </TableHead>
                                <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                  <Trans>ADA Codes</Trans>
                                </TableHead>
                                <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                  <Trans>Coverage ({planTypeLabel})</Trans>
                                </TableHead>
                                <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                  <Trans>Frequency</Trans>
                                </TableHead>
                                <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                  <Trans>History</Trans>
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(cat.services ?? []).map((s, sIdx) => {
                                const historyEntries = s.adaCode
                                  ? treatmentHistoryByCode.get(s.adaCode) ?? []
                                  : [];
                                const coveragePct =
                                  (s.coInsurance ?? cat.coInsurance ?? s.planCoverage) != null
                                    ? `${s.coInsurance ?? cat.coInsurance ?? s.planCoverage}%`
                                    : PLACEHOLDER_PCT;
                                return (
                                  <TableRow key={`cat-${catIdx}-s-${sIdx}`}>
                                    <TableCell className="vai-text-sm vai-text-foreground">
                                      {s.serviceName}
                                    </TableCell>
                                    <TableCell className="vai-text-sm vai-text-foreground">
                                      {s.adaCode ?? PLACEHOLDER}
                                    </TableCell>
                                    <TableCell className="vai-text-sm vai-text-foreground">
                                      {coveragePct}
                                    </TableCell>
                                    <TableCell className="vai-text-sm vai-text-foreground">
                                      <span className="vai-flex vai-items-center vai-gap-1.5">
                                        {s.frequency ?? PLACEHOLDER}
                                        {s.frequency && (
                                          <FontAwesomeIcon
                                            icon={faTriangleExclamation}
                                            className="vai-h-3 vai-w-3 vai-text-red-500"
                                            aria-hidden
                                          />
                                        )}
                                      </span>
                                    </TableCell>
                                    <TableCell className="vai-text-sm vai-text-foreground">
                                      {historyEntries.length > 0 ? (
                                        <div className="vai-space-y-0.5">
                                          {historyEntries.map((h, hIdx) => (
                                            <div
                                              key={hIdx}
                                              className="vai-flex vai-items-center vai-gap-1.5 vai-text-xs"
                                            >
                                              <FontAwesomeIcon
                                                icon={faTooth}
                                                className="vai-h-3 vai-w-3 vai-text-gray-400"
                                                aria-hidden
                                              />
                                              <span className="vai-text-gray-600">
                                                {h.location}
                                              </span>
                                              <span className="vai-text-gray-500">
                                                {h.date}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : null}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <FullReportEmptySection
                title="Coverage Details"
                message="No coverage details to display."
              />
            )}

            {/* Maximums & Deductibles */}
            {maximumsAndDeductibles &&
            (maximumsAndDeductibles.maximums.length > 0 ||
              maximumsAndDeductibles.deductibles.length > 0) ? (
              <div id="maximums-deductibles-section" className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
                <h3 className="vai-mb-6 vai-font-bold vai-text-gray-900">
                  <Trans>Maximums &amp; Deductibles</Trans>
                </h3>
                <div className="vai-space-y-6">
                  {maximumsAndDeductibles.maximums.length > 0 && (
                    <div>
                      <h4 className="vai-mb-3 vai-font-bold vai-text-gray-900">
                        <Trans>Maximums</Trans>
                      </h4>
                      <div className="vai-overflow-hidden vai-rounded-lg vai-border vai-border-gray-200">
                        <Table>
                          <TableHeader>
                            <TableRow className="vai-bg-gray-50">
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Service Type</Trans>
                              </TableHead>
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Individual</Trans>
                              </TableHead>
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Family</Trans>
                              </TableHead>
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Plan Period</Trans>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {maximumsAndDeductibles.maximums.map((row, i) => (
                              <TableRow key={i}>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.serviceType}
                                </TableCell>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.individual != null
                                    ? formatCurrency(row.individual)
                                    : PLACEHOLDER}
                                </TableCell>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.family != null
                                    ? formatCurrency(row.family)
                                    : PLACEHOLDER}
                                </TableCell>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.planPeriod ?? PLACEHOLDER}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  {maximumsAndDeductibles.deductibles.length > 0 && (
                    <div>
                      <h4 className="vai-mb-3 vai-font-bold vai-text-gray-900">
                        <Trans>Deductibles</Trans>
                      </h4>
                      <div className="vai-overflow-hidden vai-rounded-lg vai-border vai-border-gray-200">
                        <Table>
                          <TableHeader>
                            <TableRow className="vai-bg-gray-50">
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Service Type</Trans>
                              </TableHead>
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Individual</Trans>
                              </TableHead>
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Family</Trans>
                              </TableHead>
                              <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                                <Trans>Plan Period</Trans>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {maximumsAndDeductibles.deductibles.map((row, i) => (
                              <TableRow key={i}>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.serviceType}
                                </TableCell>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.individual != null
                                    ? formatCurrency(row.individual)
                                    : PLACEHOLDER}
                                </TableCell>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.family != null
                                    ? formatCurrency(row.family)
                                    : PLACEHOLDER}
                                </TableCell>
                                <TableCell className="vai-text-sm vai-text-foreground">
                                  {row.planPeriod ?? PLACEHOLDER}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <FullReportEmptySection
                title="Maximums & Deductibles"
                message="No maximums & deductibles to display."
              />
            )}

            {/* Limitations & Disclaimers */}
            {disclaimersAndLimitations &&
            (disclaimersAndLimitations.generalDisclaimers.length > 0 ||
              disclaimersAndLimitations.categoryLimitations.length > 0) ? (
              <div id="limitations-and-disclaimers-section" className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
                <h3 className="vai-mb-6 vai-font-bold vai-text-gray-900">
                  <Trans>Limitations &amp; Disclaimers</Trans>
                </h3>
                <div className="vai-space-y-6">
                  {disclaimersAndLimitations.generalDisclaimers.length > 0 && (
                    <ul className="vai-list-disc vai-pl-5 vai-space-y-1 vai-text-sm vai-text-gray-700">
                      {disclaimersAndLimitations.generalDisclaimers.map((d, i) => (
                        <li key={i}>{d.text}</li>
                      ))}
                    </ul>
                  )}
                  {disclaimersAndLimitations.categoryLimitations.filter((cat) => cat.categoryName !== 'Frequency').map((cat, catIdx) => (
                    <div key={catIdx}>
                      <h4 className="vai-mb-3 vai-font-bold vai-text-gray-900">
                        {cat.categoryName}
                      </h4>
                      <div className="vai-space-y-2">
                        {cat.limitations.map((lim, limIdx) => (
                          <div
                            key={limIdx}
                            className="vai-flex vai-items-center vai-gap-3"
                          >
                            <span className="vai-inline-flex vai-items-center vai-rounded-md vai-border vai-border-red-200 vai-bg-red-50 vai-px-2 vai-py-0.5 vai-text-xs vai-font-medium vai-text-red-700">
                              {lim.procedureCode}
                            </span>
                            <span className="vai-text-sm vai-text-gray-700">
                              {lim.limitation}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {disclaimersAndLimitations.footerNote && (
                    <p className="vai-text-xs vai-text-muted-foreground vai-italic">
                      {disclaimersAndLimitations.footerNote}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <FullReportEmptySection
                title="Limitations & Disclaimers"
                message="No limitations or disclaimers to display."
              />
            )}

            {/* Waiting Periods */}
            {waitingPeriods.length > 0 ? (
              <div id="waiting-periods-section" className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
                <h3 className="vai-mb-4 vai-font-bold vai-text-gray-900">
                  <Trans>Waiting Periods</Trans>
                </h3>
                <div className="vai-overflow-hidden vai-rounded-lg vai-border vai-border-gray-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="vai-bg-gray-50">
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>Service Type</Trans>
                        </TableHead>
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>Start Date</Trans>
                        </TableHead>
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>End Date</Trans>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {waitingPeriods.map((wp, i) => (
                        <TableRow key={i}>
                          <TableCell className="vai-text-sm vai-text-foreground">
                            {wp.serviceType}
                          </TableCell>
                          <TableCell className="vai-text-sm vai-text-foreground">
                            {formatDateOnly(wp.startDate)}
                          </TableCell>
                          <TableCell className="vai-text-sm vai-text-foreground">
                            {formatDateOnly(wp.endDate)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <FullReportEmptySection
                title="Waiting Periods"
                message="No waiting periods to display."
              />
            )}

            {/* Treatment History */}
            {treatmentHistoryRows.length > 0 ? (
              <div id="treatment-history-section" className="vai-rounded-lg vai-border vai-border-gray-200 vai-bg-white vai-p-6 vai-shadow-sm">
                <h3 className="vai-mb-4 vai-font-bold vai-text-gray-900">
                  <Trans>Treatment History</Trans>
                </h3>
                <div className="vai-overflow-hidden vai-rounded-lg vai-border vai-border-gray-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="vai-bg-gray-50">
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>ADA Code</Trans>
                        </TableHead>
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>Procedure</Trans>
                        </TableHead>
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>Tooth/Quad</Trans>
                        </TableHead>
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>Surface</Trans>
                        </TableHead>
                        <TableHead className="vai-text-left vai-font-medium vai-text-muted-foreground vai-text-xs vai-uppercase">
                          <Trans>Date</Trans>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {treatmentHistoryRows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                            {row.code}
                          </TableCell>
                          <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                            {row.procedure}
                          </TableCell>
                          <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                            {row.location}
                          </TableCell>
                          <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                            {row.surface}
                          </TableCell>
                          <TableCell className="vai-text-sm vai-text-foreground vai-py-2">
                            {row.date}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <FullReportEmptySection
                title="Treatment History"
                message="No treatment history to display."
              />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
